import React, { useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { rapportService } from '../../api/services';
import { MargeRapport } from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

/**
 * Reporting de marge (TF-011). S'appuie sur le CMUP déjà maintenu par article :
 * marge = revenu HT − coût HT (CMUP × quantité), agrégé par produit ou par client.
 */
export function RapportMarge() {
  const today = new Date();
  const firstOfYear = new Date(today.getFullYear(), 0, 1);
  const iso = (d: Date) => d.toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(iso(firstOfYear));
  const [endDate, setEndDate] = useState(iso(today));
  const [groupBy, setGroupBy] = useState<'produit' | 'client'>('produit');
  const [data, setData] = useState<MargeRapport | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setData(await rapportService.getMarge(startDate, endDate, groupBy));
    } catch (e) {
      toast.error('Impossible de charger le rapport de marge');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy]);

  const tauxColor = (t: number | null) =>
    t == null ? 'text-gray-400' : t >= 30 ? 'text-green-600' : t >= 10 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Rapport de marge</h1>
        <p className="page-subtitle">Revenu HT − coût (CMUP × quantité), par {groupBy}</p>
      </div>

      <div className="card flex flex-col sm:flex-row sm:items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Du</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                 className="input-field" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Au</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                 className="input-field" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Regrouper par</label>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as 'produit' | 'client')}
                  className="input-field">
            <option value="produit">Produit</option>
            <option value="client">Client</option>
          </select>
        </div>
        <button onClick={load} className="btn-primary">
          <ArrowPathIcon className="h-4 w-4 mr-1" /> Actualiser
        </button>
      </div>

      {loading && <LoadingSpinner />}

      {!loading && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="card">
              <p className="text-sm text-gray-500">Revenu HT</p>
              <MoneyDisplay amount={data.totalRevenuHT} className="text-xl font-semibold" />
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">Coût HT</p>
              <MoneyDisplay amount={data.totalCoutHT} className="text-xl font-semibold" />
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">Marge</p>
              <MoneyDisplay amount={data.totalMarge} className="text-xl font-semibold text-green-700" />
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">Taux de marge</p>
              <p className={`text-xl font-semibold ${tauxColor(data.tauxMargePct)}`}>
                {data.tauxMargePct == null ? '—' : `${data.tauxMargePct.toFixed(2)} %`}
              </p>
            </div>
          </div>

          <div className="card overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-4 py-2">{groupBy === 'client' ? 'Client' : 'Produit'}</th>
                  <th className="px-4 py-2 text-right">Quantité</th>
                  <th className="px-4 py-2 text-right">Revenu HT</th>
                  <th className="px-4 py-2 text-right">Coût HT</th>
                  <th className="px-4 py-2 text-right">Marge</th>
                  <th className="px-4 py-2 text-right">Taux</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.lignes.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                    Aucune vente sur la période
                  </td></tr>
                )}
                {data.lignes.map((l) => (
                  <tr key={`${groupBy}-${l.id}`} className="text-sm">
                    <td className="px-4 py-2">
                      {l.libelle}
                      {l.coutIncomplet && (
                        <span className="ml-2 text-xs text-amber-600" title="Coût partiellement inconnu (CMUP manquant) : marge surévaluée">
                          ⚠ coût incomplet
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">{l.quantite}</td>
                    <td className="px-4 py-2 text-right"><MoneyDisplay amount={l.revenuHT} /></td>
                    <td className="px-4 py-2 text-right"><MoneyDisplay amount={l.coutHT} /></td>
                    <td className="px-4 py-2 text-right font-medium"><MoneyDisplay amount={l.marge} /></td>
                    <td className={`px-4 py-2 text-right font-medium ${tauxColor(l.tauxMargePct)}`}>
                      {l.tauxMargePct == null ? '—' : `${l.tauxMargePct.toFixed(2)} %`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default RapportMarge;
